// @ts-strict-ignore
import { useQuery } from '@tanstack/react-query';
import { routes } from '~/helpers/routes';
import { ajaxFetch } from '~/helpers/api';
import type { AccessType } from '~/admin/embedded-finance/types';
import log from '~/helpers/logging';
import { useSnackBar } from '@usealloy/component-library';

export interface ChildCustomer {
  access_type: AccessType;
  customer_id: number;
  is_autonomous_child: boolean;
  is_controlled_child: boolean;
  organization_name: string;
  organization_name_slug: string;
}

export type ChildCustomersApiResponse = {
  parent_organization_slug?: string;
  child_customers: ChildCustomer[];
} | undefined;

/**
 * Return a list of all child customers belonging to the current parent organization
 */
export const useGetChildCustomersKey = [routes.v3.afef.childCustomers] as const;
export const useGetChildCustomers = (
  { silent, isParent, enabled = true }:
  { silent?: boolean; isParent: boolean; enabled?: boolean },
) => {
  const addSnackBar = useSnackBar();
  const silentVal = silent ?? true;
  const [afefChildCustomersUrl] = useGetChildCustomersKey;

  const {
    data, isLoading, error, isError, fetchStatus,
  } = useQuery({
    enabled: isParent && enabled,
    queryKey: useGetChildCustomersKey,
    queryFn: async () => {
      const response = await ajaxFetch(
        afefChildCustomersUrl, { silent: silentVal },
      ) as ChildCustomersApiResponse;

      // @ts-ignore
      if (response?.status === 403) {
        return Promise.reject(response);
      }

      return response;
    },
    onError: err => {
      log('error', 'Failed to fetch child customer data', {}, err);
      addSnackBar({ title: 'Failed to fetch child customer data', type: 'danger' });
    },
    select: queryData => ({
      ...queryData,
      child_customers: queryData.child_customers
        .sort((a, b) => a.organization_name.localeCompare(b.organization_name)),
    }),
  });

  return {
    data, isLoading: isLoading && fetchStatus !== 'idle', error, isError,
  };
};

